import { useReducedMotion } from "motion/react";
import {
  Bar,
  BarChart as RBarChart,
  Cell,
  Line,
  LineChart as RLineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { RollingText } from "./rolling-text";

const tooltipStyle = {
  background: "var(--surface-raised)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 18,
  color: "var(--foreground)",
  fontSize: 13,
};

/** A data point: its label, its value, and the value's text when a number alone would mislead (money, a unit). */
export type ChartDatum = { label: string; value: number; text?: string };

// Data marks take the theme's chart colour when it has one: status colours never mark data.
const MARK = "var(--chart, var(--accent))";

/** The values as text for assistive tech, which reads no chart drawing. */
function ChartText({ label, data }: { label?: string; data: ChartDatum[] }) {
  return (
    <ul className="sr-only" aria-label={label}>
      {data.map((d, i) => (
        <li key={`${d.label}-${i}`}>{d.label}: {d.text ?? d.value.toLocaleString("en-US")}</li>
      ))}
    </ul>
  );
}

const textOf = (value: unknown, _name: unknown, item: { payload?: ChartDatum }) => item.payload?.text ?? String(value);

export function LineChart({ data, label }: { data: ChartDatum[]; label?: string }) {
  const reduce = useReducedMotion();
  return (
    <figure data-slot="line-chart" className="m-0 w-full">
      <div aria-hidden className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RLineChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }} accessibilityLayer={false}>
            <XAxis dataKey="label" tick={{ fill: "var(--text-muted)", fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "var(--text-muted)", fontSize: 12 }} axisLine={false} tickLine={false} width={48} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: "var(--border)" }} formatter={textOf} />
            <Line type="monotone" dataKey="value" stroke={MARK} strokeWidth={2} dot={false} isAnimationActive={!reduce} animationDuration={240} />
          </RLineChart>
        </ResponsiveContainer>
      </div>
      <ChartText label={label} data={data} />
    </figure>
  );
}

export function BarChart({ data, label }: { data: ChartDatum[]; label?: string }) {
  const reduce = useReducedMotion();
  return (
    <figure data-slot="bar-chart" className="m-0 w-full">
      <div aria-hidden className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RBarChart data={data} margin={{ top: 12, right: 8, left: 0, bottom: 0 }} accessibilityLayer={false}>
            <XAxis dataKey="label" tick={{ fill: "var(--text-muted)", fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: "var(--text-muted)", fontSize: 12 }} axisLine={false} tickLine={false} width={48} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "var(--surface-muted)" }} formatter={textOf} />
            <Bar dataKey="value" fill={MARK} radius={[8, 8, 0, 0]} isAnimationActive={!reduce} animationDuration={240} />
          </RBarChart>
        </ResponsiveContainer>
      </div>
      <ChartText label={label} data={data} />
    </figure>
  );
}

const slices = ["var(--accent)", "var(--info)", "var(--success)", "var(--warning)"];

export function DonutChart({ data }: { data: { label: string; value: number }[] }) {
  const reduce = useReducedMotion();
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return (
    <div data-slot="donut-chart" className="grid items-center gap-4 sm:grid-cols-[16rem_1fr]">
      <div className="relative h-56">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="label" innerRadius={62} outerRadius={84} paddingAngle={3} isAnimationActive={!reduce} animationDuration={240} stroke="none">
              {data.map((item, index) => (
                <Cell key={item.label} fill={slices[index % slices.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div>
            <p className="text-xs text-muted">Total</p>
            <p className="font-display text-2xl tracking-[-0.03em]">
              <RollingText text={total.toLocaleString("en-US")} />
            </p>
          </div>
        </div>
      </div>
      <ul className="grid gap-2 text-sm">
        {data.map((item, index) => (
          <li key={item.label} className="flex items-center justify-between gap-3">
            <span className="inline-flex items-center gap-2">
              <span className="size-2.5 rounded-pill" style={{ background: slices[index % slices.length] }} />
              {item.label}
            </span>
            <span className="tabular-nums text-secondary">{item.value.toLocaleString("en-US")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Sparkline({ data, label = "Trend" }: { data: number[]; label?: string }) {
  const reduce = useReducedMotion();
  const points = data.map((value, index) => ({ index, value }));
  const summary = data.length > 1 ? `${label}, from ${data[0]} to ${data[data.length - 1]}` : label;
  return (
    <div data-slot="sparkline" className="h-10 w-28" role="img" aria-label={summary}>
      <ResponsiveContainer width="100%" height="100%">
        <RLineChart data={points} accessibilityLayer={false}>
          <Line type="monotone" dataKey="value" stroke="var(--accent)" strokeWidth={2} dot={false} isAnimationActive={!reduce} animationDuration={180} />
        </RLineChart>
      </ResponsiveContainer>
    </div>
  );
}
